import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, Plus, 
  User, Users, Calendar, Heart
} from 'lucide-react';

// Mock data
const mockMessages = [
  {
    id: 1,
    user: 'Sarah Ahmed',
    avatar: 'SA',
    color: 'bg-purple-500',
    message: 'Great work on the Ice Karkade campaign! The new visuals are exactly what we were looking for.',
    time: '2 hours ago',
    likes: 5,
    replies: 2
  },
  {
    id: 2,
    user: 'Mohammed Ali',
    avatar: 'MA',
    color: 'bg-blue-500',
    message: 'Thanks Sarah! I\'ve uploaded the final files to the shared drive. Ready for review.',
    time: '1 hour ago',
    likes: 3,
    replies: 1
  },
  {
    id: 3,
    user: 'Fatima Hassan',
    avatar: 'FH',
    color: 'bg-green-500',
    message: 'Can someone help me with the brand guidelines for the new product line?',
    time: '30 minutes ago',
    likes: 1,
    replies: 0
  }
];

const mockTasks = [
  {
    id: 1,
    title: 'Review Ice Karkade campaign assets',
    description: 'Final review of all creative assets before launch',
    assignee: 'Sarah Ahmed',
    dueDate: '2024-06-10',
    priority: 'high',
    status: 'in_progress',
    progress: 75
  },
  {
    id: 2,
    title: 'Design new product packaging',
    description: 'Create packaging design for Black Coffee line',
    assignee: 'Mohammed Ali',
    dueDate: '2024-06-15',
    priority: 'medium',
    status: 'todo',
    progress: 0
  },
  {
    id: 3,
    title: 'Update brand guidelines',
    description: 'Incorporate new brand elements into guidelines',
    assignee: 'Fatima Hassan',
    dueDate: '2024-06-20',
    priority: 'low',
    status: 'completed',
    progress: 100
  }
];

const Collaboration = ({ isDarkMode = true }) => {
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('general');
  const [newMessage, setNewMessage] = useState('');

  // Mock data
  const channels = [
    { id: 'general', name: 'General', unread: 3, color: 'bg-blue-500' },
    { id: 'design', name: 'Design Team', unread: 0, color: 'bg-purple-500' },
    { id: 'campaigns', name: 'Campaigns', unread: 5, color: 'bg-green-500' },
    { id: 'reviews', name: 'Design Reviews', unread: 2, color: 'bg-orange-500' }
  ];

  const teamMembers = [
    { id: 1, name: 'Sarah Ahmed', role: 'Creative Director', avatar: 'SA', online: true, color: 'bg-purple-500' },
    { id: 2, name: 'Mohammed Ali', role: 'Senior Designer', avatar: 'MA', online: true, color: 'bg-blue-500' },
    { id: 3, name: 'Fatima Hassan', role: 'Graphic Designer', avatar: 'FH', online: false, color: 'bg-green-500' },
    { id: 4, name: 'Ahmed Omar', role: 'Marketing Manager', avatar: 'AO', online: true, color: 'bg-orange-500' }
  ];

  useEffect(() => {
    setMessages(mockMessages);
    setTasks(mockTasks);
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700';
      case 'medium': return isDarkMode ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-700';
      case 'low': return isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700';
      default: return isDarkMode ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return isDarkMode ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-100 text-gray-700';
      case 'in_progress': return isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700';
      case 'completed': return isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700';
      default: return isDarkMode ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };

  const MessageItem = ({ message }) => (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className={`flex space-x-4 p-4 backdrop-blur-sm rounded-2xl transition-all duration-300 border ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:bg-black/30' 
          : 'bg-white/60 border-white/20 hover:bg-white/80'
      }`}
    >
      <div className={`w-10 h-10 rounded-full ${message.color} flex items-center justify-center text-white font-semibold text-sm`}>
        {message.avatar}
      </div>
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-2">
          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{message.user}</span>
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{message.time}</span>
        </div>
        <p className={`mb-3 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{message.message}</p>
        <div className={`flex items-center space-x-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          <button className="flex items-center space-x-1 hover:text-red-500 transition-colors">
            <Heart className="h-4 w-4" />
            <span>{message.likes}</span>
          </button>
          <button className="flex items-center space-x-1 hover:text-blue-500 transition-colors">
            <MessageSquare className="h-4 w-4" />
            <span>{message.replies}</span>
          </button>
          <button className="hover:text-blue-600 transition-colors">Reply</button>
        </div>
      </div>
    </motion.div>
  );

  const TaskItem = ({ task }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border transition-all duration-300 ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:shadow-xl hover:bg-black/30' 
          : 'bg-white/80 border-white/20 hover:shadow-xl hover:bg-white/90'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{task.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
              {task.priority}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
              {task.status.replace('_', ' ')}
            </span>
          </div>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{task.description}</p>
          <div className={`flex items-center space-x-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <div className="flex items-center space-x-1">
              <User className="h-4 w-4" />
              <span>{task.assignee}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>{new Date(task.dueDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Progress</span>
          <span className="font-medium">{task.progress}%</span>
        </div>
        <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${task.progress}%` }}
          />
        </div>
      </div>
    </motion.div>
  );

  const ChannelList = () => (
    <div className="space-y-2">
      {channels.map((channel) => (
        <motion.button
          key={channel.id}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setSelectedChannel(channel.id)}
          className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${
            selectedChannel === channel.id
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
              : isDarkMode
                ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/10'
                : 'bg-white/60 backdrop-blur-sm hover:bg-white/80 border border-white/20'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${channel.color}`} />
            <span className={`font-medium ${
              selectedChannel === channel.id 
                ? 'text-white' 
                : isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{channel.name}</span>
          </div>
          {channel.unread > 0 && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              selectedChannel === channel.id
                ? 'bg-white/20 text-white'
                : isDarkMode 
                  ? 'bg-red-500/20 text-red-300' 
                  : 'bg-red-100 text-red-600'
            }`}>
              {channel.unread}
            </span>
          )}
        </motion.button>
      ))}
    </div>
  );

  const TeamMembers = () => (
    <div className="space-y-3">
      {teamMembers.map((member) => (
        <motion.div
          key={member.id}
          whileHover={{ scale: 1.02 }}
          className={`flex items-center space-x-3 p-3 rounded-xl border transition-all duration-300 ${
            isDarkMode 
              ? 'bg-white/10 backdrop-blur-sm border-white/10 hover:bg-white/20' 
              : 'bg-white/60 backdrop-blur-sm border-white/20 hover:bg-white/80'
          }`}
        >
          <div className="relative">
            <div className={`w-10 h-10 rounded-full ${member.color} flex items-center justify-center text-white font-semibold text-sm`}>
              {member.avatar}
            </div>
            {member.online && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
            )}
          </div>
          <div className="flex-1">
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{member.name}</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{member.role}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Team Collaboration
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Connect, communicate, and collaborate with your creative team
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          onClick={() => {
            // TODO: Implement create task functionality
          }}
        >
          <Plus className="h-5 w-5" />
          <span>New Task</span>
        </motion.button>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Left Sidebar - Channels & Team */}
        <div className="lg:col-span-1 space-y-6">
          {/* Channels */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
              isDarkMode 
                ? 'bg-black/20 border-white/10' 
                : 'bg-white/80 border-gray-200/50'
            }`}
          >
            <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Channels</h2>
            <ChannelList />
          </motion.div>

          {/* Team Members */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
              isDarkMode 
                ? 'bg-black/20 border-white/10' 
                : 'bg-white/80 border-gray-200/50'
            }`}
          >
            <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Team Members</h2>
            <TeamMembers />
          </motion.div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
              isDarkMode 
                ? 'bg-black/20 border-white/10' 
                : 'bg-white/80 border-gray-200/50'
            }`}
          >
            <div className={`flex space-x-1 p-1 rounded-lg ${
              isDarkMode ? 'bg-black/30 border border-white/10' : 'bg-gray-100'
            }`}>
              {['messages', 'tasks'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? isDarkMode 
                        ? 'bg-white/20 text-white shadow-sm' 
                        : 'bg-white text-purple-600 shadow-sm'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'messages' ? (
              <motion.div
                key="messages"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <MessageItem message={message} />
                  </motion.div>
                ))}
                
                {/* Message Input */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className={`backdrop-blur-xl rounded-2xl p-4 shadow-lg border ${
                    isDarkMode 
                      ? 'bg-black/20 border-white/10' 
                      : 'bg-white/80 border-gray-200/50'
                  }`}
                >
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className={`flex-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 ${
                        isDarkMode 
                          ? 'bg-black/20 border border-white/10 text-white placeholder-gray-400' 
                          : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300">
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="tasks"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {tasks.map((task, index) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TaskItem task={task} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Collaboration; 